import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { filter, merge, pipe, switchMap, take, tap } from 'rxjs';
import { GroupService } from '@thiememeulenhoff/ng-api-gateway';
import { Group } from '@thiememeulenhoff/types';
import { UserSettingFacade } from '../user-setting';
import {
  RequestStatus,
  withRequestStatus,
} from '../_utils/request-status.feature';

type GroupState = {
  groups: Array<Group>;
  selectedGroupId: string;
  arrangementCode: string | null;
};

const initialState: GroupState = {
  groups: [],
  selectedGroupId: '',
  arrangementCode: null,
};

export const GroupStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withComputed(({ groups, selectedGroupId }) => ({
    selectedGroup: computed(() =>
      groups().find((g) => g.id === selectedGroupId()),
    ),
    validatedSelectedGroupId: computed(() => {
      const selected = String(selectedGroupId());
      return String(groups().find((g) => String(g.id) === selected)?.id ?? '');
    }),
  })),
  withMethods(
    (
      store,
      groupService = inject(GroupService),
      userSettingFacade = inject(UserSettingFacade),
    ) => {
      const fetchGroupsFromService = (scope: string) =>
        groupService.fetchGroups(scope).pipe(
          tap(() => patchState(store, { requestStatus: 'loading' })),
          tapResponse({
            next: (groupsIndex) =>
              patchState(store, {
                groups: groupsIndex.items,
                requestStatus: 'success',
              }),
            error: (error: Error) =>
              patchState(store, {
                requestStatus: {
                  error: error.message || 'Failed to fetch',
                },
              }),
          }),
        );

      return {
        fetchGroup: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { requestStatus: 'loading' })),
            switchMap((id) =>
              groupService.fetchGroup(id).pipe(
                tapResponse({
                  next: (group) =>
                    patchState(store, (state) => ({
                      groups: state.groups.some((g) => g.id === group.id)
                        ? state.groups.map((g) =>
                            g.id === group.id ? group : g,
                          )
                        : [...state.groups, group],
                      requestStatus: 'success' as RequestStatus,
                    })),
                  error: (error: Error) =>
                    patchState(store, {
                      requestStatus: {
                        error: error.message || 'Failed to fetch',
                      },
                    }),
                }),
              ),
            ),
          ),
        ),
        setArrangementCodeAndMethodSlugAndFetch: rxMethod<{
          arrangementCode: string;
          methodSlug: string;
        }>(
          pipe(
            filter(
              ({ arrangementCode }) =>
                store.arrangementCode() !== arrangementCode,
            ),
            tap(({ arrangementCode }) =>
              patchState(store, { arrangementCode, requestStatus: 'loading' }),
            ),
            switchMap(({ arrangementCode, methodSlug }) =>
              merge(
                fetchGroupsFromService(methodSlug),
                userSettingFacade
                  .getUserSetting(`selected-group-${arrangementCode}`)
                  .pipe(
                    take(1),
                    tapResponse({
                      next: (setting) =>
                        patchState(store, {
                          selectedGroupId: setting?.value ?? '',
                          requestStatus: 'success',
                        }),
                      error: (error: Error) =>
                        patchState(store, {
                          requestStatus: {
                            error: error.message || 'Failed to fetch',
                          },
                        }),
                    }),
                  ),
              ),
            ),
          ),
        ),
        selectGroup: rxMethod<string>(
          pipe(
            filter(
              (id) =>
                store.arrangementCode() !== null &&
                store.selectedGroupId() !== id,
            ),
            tap((id) => patchState(store, { selectedGroupId: id })),
            switchMap((id) =>
              userSettingFacade
                .setUserSetting(
                  {
                    key: `selected-group-${store.arrangementCode()}`,
                    value: id,
                  },
                  true,
                )
                .pipe(take(1)),
            ),
          ),
        ),
      };
    },
  ),
);
